import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, MenuIcon, XIcon } from 'lucide-react';
import { LOGO_URL, headerNavLinks } from '../data/homepage';

// Full desktop nav needs ~1190px; below this width the header collapses to the menu button.
const DESKTOP_NAV_MIN = 1280;
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById('top');
      const limit = hero ? hero.offsetTop + hero.offsetHeight - 120 : 400;
      setAtTop(window.scrollY < limit);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!open) return;
    firstLinkRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onResize = () => {
      if (window.innerWidth >= DESKTOP_NAV_MIN) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);
  return <>
      {/* Non-sticky service statement */}
      <div className="bg-mist text-ink" data-id="element-181" data-mp="20qguyv91s2ki">
        <p className="mx-auto max-w-[1280px] px-5 py-2 text-center text-[12px] font-semibold leading-[1.45] md:px-8" data-id="element-182" data-mp="20pnn1qycr70s">
          <span className="md:hidden" data-id="element-183" data-mp="1832wgsppayya">Custom websites, ecommerce, SEO, integrations, hosting, and ongoing management.</span>
          <span className="hidden md:inline" data-id="element-184" data-mp="1d14edabe4s86">
            Custom websites, ecommerce, SEO, Google Ads support, lead generation, integrations, hosting, and ongoing
            website management.
          </span>
        </p>
      </div>
      <header className="on-dark sticky top-0 z-40 bg-ink text-white" data-id="element-185" data-mp="101v8n1ge4qkg">
        <div className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between gap-6 px-5 md:px-8 xl:h-20" data-id="element-186" data-mp="1epzvmhmh7o91">
          <a href="#top" className="flex shrink-0 items-center gap-4" aria-label="WebsiteForge, custom built since 2002, back to top" data-id="element-187" data-mp="19sgftda4r3ez">
            <img src={LOGO_URL} alt="" className="h-10 w-auto xl:h-11" width={196} height={44} data-id="element-188" data-mp="h3altllj87cm" />
            <span className="hidden h-9 w-px bg-white/25 sm:block" aria-hidden data-id="element-189" data-mp="w0jsdmjephox" />
            <span className="hidden text-[11px] font-bold uppercase leading-[1.35] text-white/75 sm:block" aria-hidden data-id="element-190" data-mp="vygrg7gpu45f">
              Custom built
              <br data-id="element-191" data-mp="27excqheybf07" />
              Since 2002
            </span>
          </a>

          <nav aria-label="Primary" className="hidden xl:block" data-id="element-192" data-mp="22glzrxtd0odr">
            <ul className="flex items-center gap-7" data-id="element-193" data-mp="22gcdak7iwzct">
              {headerNavLinks.map(link => {
              const active = link.href === '#top' && atTop;
              return <li key={link.href} data-id="element-194" data-mp="2f0srq4qsfj4u">
                    <a href={link.href} aria-current={active ? 'location' : undefined} className={`relative whitespace-nowrap py-2 text-[15px] font-semibold transition-colors duration-150 ease-out hover:text-white ${active ? 'text-white' : 'text-white/80'}`} data-id="element-195" data-mp="20zgfo4er01o8">
                      {link.label}
                      <span aria-hidden className={`absolute inset-x-0 -bottom-0.5 h-0.5 rounded-full bg-brand-orange transition-opacity duration-150 ease-out ${active ? 'opacity-100' : 'opacity-0'}`} data-id="element-196" data-mp="2jx9g901wfzig" />
                    </a>
                  </li>;
            })}
            </ul>
          </nav>

          <div className="flex items-center gap-3" data-id="element-197" data-mp="1st6w1e5pmw4t">
            <a href="#contact" className="group hidden items-center gap-2 whitespace-nowrap rounded-md bg-brand-orange px-5 py-3 text-[15px] font-bold text-ink transition-colors duration-150 ease-out hover:bg-brand-orangeHover sm:inline-flex" data-id="element-198" data-mp="1ns9aqspellz3">
              Discuss Your Project
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden data-id="element-199" data-mp="2bh2189n9b1mb" />
            </a>
            <button ref={toggleRef} type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-md text-white transition-colors duration-150 ease-out hover:bg-white/10 xl:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(v => !v)} data-id="element-200" data-mp="21rikb9j3thu3">
              {open ? <XIcon className="h-6 w-6" aria-hidden data-id="element-201" data-mp="gylc2z2ow9v2" /> : <MenuIcon className="h-6 w-6" aria-hidden data-id="element-202" data-mp="gzmmd1bvssbq" />}
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-203" data-mp="kzf1xxzozyql">
          {open && <motion.nav id="mobile-menu" aria-label="Mobile" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: reduceMotion ? 0 : 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-x-0 top-full border-t border-ink-line bg-ink shadow-[0_12px_24px_rgba(0,0,0,0.25)] xl:hidden" data-id="element-204" data-mp="pz9qvvzwqe90">
              <ul className="mx-auto max-w-[1280px] px-5 py-4 md:px-8" data-id="element-205" data-mp="18ozk4eo0uryt">
                {headerNavLinks.map((link, i) => {
              const active = link.href === '#top' && atTop;
              return <li key={link.href} className="border-b border-ink-line last:border-b-0" data-id="element-206" data-mp="1ilm3ez0p9ayq" data-mpi={i}>
                      <a ref={i === 0 ? firstLinkRef : undefined} href={link.href} aria-current={active ? 'location' : undefined} onClick={() => setOpen(false)} className={`flex items-center gap-3 py-4 text-[17px] font-semibold ${active ? 'text-white' : 'text-white/85'}`} data-id="element-207" data-mp="3ep0nka08oci5">
                        {active && <span className="h-4 w-0.5 rounded-full bg-brand-orange" aria-hidden data-id="element-208" data-mp="3tg9fwqvj0byz" />}
                        {link.label}
                      </a>
                    </li>;
            })}
                <li className="pt-4" data-id="element-209" data-mp="6b0e3bkfnn9m">
                  <a href="#contact" onClick={() => setOpen(false)} className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-orange px-5 py-3.5 text-[16px] font-bold text-ink" data-id="element-210" data-mp="6c30p7aq33jq">
                    Discuss Your Project
                    <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-211" data-mp="nmylawvkh1bj" />
                  </a>
                </li>
              </ul>
            </motion.nav>}
        </AnimatePresence>
      </header>
    </>;
}